import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Palette,
  Layout,
  MousePointerClick,
  Type,
  Image as ImageIcon,
  MessageSquare,
  Home,
  Sparkles,
  LifeBuoy,
  Save,
  Plus,
  Trash2,
  Crop,
  UploadCloud,
  Lock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import API_CONFIG from '../config/api';
import ChatPreview from '../components/chatbot/ChatPreview';
import ImageCropModal from '../components/ui/ImageCropModal';
import ConfirmModal from '../components/ui/ConfirmModal';
import Loader, { ButtonLoader } from '../components/ui/Loader';
import { useSubscription } from '../hooks/useSubscription';
import {
  Section,
  Field,
  Counter,
  TextInput,
  TextArea,
  SelectionCards,
  ToggleRow,
  SliderField,
  ColorField,
  SearchSelect,
} from '../components/appearance/controls';

const schema = require('../widget/widget-settings.js');

const {
  ENUMS,
  LOGO_PADDING_STEPS,
  BORDER_RADIUS,
  FONT_OPTIONS,
  SIZE_DIMENSIONS,
  DEFAULT_APPEARANCE,
  toWidgetSettings,
  validateAppearance,
  sanitizeAppearance,
} = schema;

/* ------------------------------------------------------------------ *
 * Option lists
 *
 * Every list below is derived from ENUMS rather than typed out, so the form
 * cannot offer a value the save endpoint rejects. That mismatch is what used to
 * produce a 400 when someone picked an animation style the validator had never
 * been told about.
 * ------------------------------------------------------------------ */

const TABS = [
  { id: 'style', label: 'Style' },
  { id: 'branding', label: 'Branding' },
  { id: 'content', label: 'Content' },
  { id: 'home', label: 'Home & channels' },
];

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', description: 'White panel, dark text' },
  { value: 'dark', label: 'Dark', description: 'Charcoal panel, light text' },
];

const HEADER_STYLE_OPTIONS = [
  { value: 'solid', label: 'Solid', description: 'Flat brand color' },
  { value: 'gradient', label: 'Gradient', description: 'Blends into a second color' },
];

const POSITION_OPTIONS = [
  { value: 'bottom-right', label: 'Bottom right' },
  { value: 'bottom-left', label: 'Bottom left' },
  { value: 'top-right', label: 'Top right' },
  { value: 'top-left', label: 'Top left' },
];

// Dimensions come from SIZE_DIMENSIONS, never hardcoded — these labels drifted
// from the real panel once already (cards said 350x500 while it rendered 360x560).
const SIZE_OPTIONS = ENUMS.size.map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1),
  description: `${SIZE_DIMENSIONS[value][0]} x ${SIZE_DIMENSIONS[value][1]}`,
}));

const SPACING_OPTIONS = [
  { value: 'compact', label: 'Compact', description: 'Tighter, fits more' },
  { value: 'normal', label: 'Normal', description: 'Balanced' },
  { value: 'spacious', label: 'Spacious', description: 'Roomy and calm' },
];

const LOGO_SHAPE_OPTIONS = [
  { value: 'circle', label: 'Circle' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'square', label: 'Square' },
];

const LOGO_FIT_OPTIONS = [
  { value: 'cover', label: 'Fill', description: 'Crops to fill the frame' },
  { value: 'contain', label: 'Fit', description: 'Shows the whole mark' },
];

const TYPING_OPTIONS = [
  { value: 'dots', label: 'Dots', description: 'Three blinking dots' },
  { value: 'wave', label: 'Wave', description: 'Dots ride a sine' },
  { value: 'pulse', label: 'Pulse', description: 'One radiating ring' },
];

/** Same 24x24 paths the widget draws, so the picker shows the real glyph. */
const BUTTON_ICON_PATHS = {
  'chat-circle':
    'M7.51 19.8C8.83 20.56 10.36 21 12 21c4.97 0 9-4.03 9-9s-4.03-9-9-9-9 4.03-9 9c0 1.64.44 3.17 1.2 4.49c.07.13.11.19.13.25.02.06.02.11.01.17 0 .06-.02.13-.06.26l-.77 2.31c-.16.49-.24.73-.18.89.05.14.16.25.3.3.16.06.4-.02.89-.18l2.31-.77c.13-.04.2-.06.26-.06.06-.01.11 0 .17.01.06.02.12.05.25.13z',
  note: 'M13 20c-.1 0-.2 0-.32 0H7.2c-1.12 0-1.68 0-2.11-.22a2 2 0 01-.87-.87C4 18.48 4 17.92 4 16.8V7.2c0-1.12 0-1.68.22-2.11a2 2 0 01.87-.87C5.52 4 6.08 4 7.2 4h9.6c1.12 0 1.68 0 2.11.22a2 2 0 01.87.87C20 5.52 20 6.08 20 7.2v5.47M13 20c.29 0 .47-.01.64-.05.2-.05.4-.13.58-.24.2-.12.37-.3.72-.64l4.12-4.13c.35-.34.52-.52.64-.72.11-.18.19-.38.24-.58.04-.17.05-.36.05-.64M13 20v-5.4c0-.56 0-.84.11-1.05a1 1 0 01.44-.44c.21-.11.49-.11 1.05-.11H20',
  'chat-square':
    'M5.6 19.92l1.52-1.22c.32-.25.48-.38.66-.47.16-.08.33-.14.51-.18.2-.04.41-.04.83-.04h8.68c1.12 0 1.68 0 2.11-.22a2 2 0 00.87-.87c.22-.43.22-.99.22-2.11V7.2c0-1.12 0-1.68-.22-2.11a2 2 0 00-.87-.87C19.48 4 18.92 4 17.8 4H6.2c-1.12 0-1.68 0-2.11.22a2 2 0 00-.87.87C3 5.52 3 6.08 3 7.2v11.47c0 1.07 0 1.6.22 1.87.19.24.48.38.78.38.35 0 .77-.33 1.6-.92z',
  'paper-plane':
    'M10.31 13.69l4.85-4.85M20.11 5.89l-4.09 13.29c-.37 1.19-.55 1.79-.87 1.99a1 1 0 01-.91.08c-.34-.14-.62-.7-1.18-1.82l-2.59-5.18a1 1 0 00-.19-.34 1 1 0 00-.34-.19l-5.18-2.59c-1.12-.56-1.68-.84-1.82-1.18a1 1 0 01.08-.91c.2-.32.8-.5 1.99-.87l13.29-4.09c.94-.29 1.41-.43 1.72-.32a1 1 0 01.59.59c.12.31-.03.78-.32 1.72z',
  bulb: 'M9 21h6M12 3a6 6 0 00-6 6c0 1.21.36 2.34.98 3.29.95 1.45 1.43 2.18 1.49 2.29.55.96.45.62.52 1.73 0 .12 0 .31 0 .69a1 1 0 001 1h4a1 1 0 001-1c0-.38 0-.57.01-.69.07-1.11-.03-.77.52-1.73.06-.11.54-.84 1.49-2.29A6 6 0 0012 3z',
  planet:
    'M6.9 15.16A7.48 7.48 0 0012 17c4.42 0 8-3.58 8-8 0-.57-.06-1.12-.17-1.66M6.9 15.16c-1.77-1.47-2.9-3.68-2.9-6.16 0-4.42 3.58-8 8-8 3.85 0 7.06 2.72 7.83 6.34M6.9 15.16c1.95-.45 4.28-1.38 6.63-2.73 2.73-1.58 4.95-3.42 6.3-5.09M6.9 15.16c-2.43.56-4.29.36-4.9-.72-.63-1.09.14-2.87 2-4.75M19.83 7.34c1.25-1.55 1.76-2.96 1.22-3.88-.54-.93-2.04-1.19-4.05-.86',
  leaf: 'M6.83 17.08c7.07 4.24 12.73-1.41 12.02-12.02-10.61-.71-16.26 4.95-12.02 12.02zm0 0L5 18.91m1.83-1.83l3.83-3.83',
};

const BUTTON_ICON_OPTIONS = ENUMS.buttonIcon.map((value) => ({
  value,
  label: value
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' '),
  preview: (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-gray-700 dark:text-gray-200"
    >
      <path d={BUTTON_ICON_PATHS[value]} />
    </svg>
  ),
}));

const FONT_CHOICES = FONT_OPTIONS.map((f) => ({ value: f, label: f }));

const DEFAULT_VIEW_OPTIONS = [
  { value: 'home', label: 'Home screen', description: 'Starters and contact options first' },
  { value: 'chat', label: 'Chat', description: 'Straight into the conversation' },
];

const RADIUS_PRESETS = [
  { label: 'S', value: 8 },
  { label: 'M', value: 18 },
  { label: 'L', value: 28 },
];

const MAX_STARTERS = 6;
const STARTER_MAX_CHARS = 80;
const NAME_MAX_CHARS = 40;

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

export default function AppearancePage() {
  const { canAccessFeature } = useSubscription();
  // Matches the server-side gate in publicWidgetController: Pro only. Anything
  // else and the widget serves the branding strip regardless of this toggle.
  const canHideBranding = canAccessFeature('custom_branding');

  const [tab, setTab] = useState('style');
  const [appearance, setAppearance] = useState(DEFAULT_APPEARANCE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  // Errors only appear after a save attempt — flagging a half-typed field while
  // the merchant is still typing in it reads as the form fighting them.
  const [showErrors, setShowErrors] = useState(false);

  const savedRef = useRef(DEFAULT_APPEARANCE);

  const set = useCallback((key, value) => {
    setAppearance((prev) => ({ ...prev, [key]: value }));
  }, []);

  /* ---- load ---- */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await axios.get(`${API_CONFIG.BASE_URL}/api/appearance`, {
          headers: { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' },
          params: { _t: Date.now() },
        });
        if (cancelled) return;
        if (res.data?.success && res.data.data?.appearance) {
          // Sanitised here as well as on the server. A row written before the
          // current option set (a retired font, a corner radius of 32 from the
          // old slider) would otherwise load straight into the form and fail
          // the save gate on a field nobody touched. Doing it client-side too
          // means the page recovers against a backend that has not been
          // redeployed yet, since what it sends is already valid.
          const loaded = sanitizeAppearance(res.data.data.appearance);
          setAppearance(loaded);
          savedRef.current = loaded;
        }
      } catch {
        toast.error('Could not load your widget settings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /* ---- dirty tracking ---- */
  const dirty = useMemo(
    () => JSON.stringify(appearance) !== JSON.stringify(savedRef.current),
    [appearance],
  );

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /* ---- preview settings ---- */
  // The preview reads exactly what the widget will read. Memoised on the
  // appearance object so ChatPreview.update() only fires on a real change.
  const previewSettings = useMemo(
    () => toWidgetSettings(appearance, { apiBase: API_CONFIG.BASE_URL }),
    [appearance],
  );

  /* ---- save ---- */
  const handleSave = async () => {
    const found = validateAppearance(appearance);
    setErrors(found);
    setShowErrors(true);

    const bad = Object.keys(found);
    if (bad.length) {
      // Jump to the tab that actually holds the problem instead of leaving the
      // merchant to hunt for a red field on a tab they cannot see.
      const tabFor = {
        style: ['primaryColor', 'gradientColor', 'buttonBgColor', 'buttonIconColor', 'borderRadius', 'fontFamily', 'theme', 'headerStyle', 'position', 'size', 'messageSpacing', 'buttonIcon', 'defaultView'],
        branding: ['botName', 'logoShape', 'logoFit', 'logoPadding'],
        content: ['customSuggestions', 'typingIndicator'],
        home: ['whatsappNumber', 'emailAddress', 'phoneNumber', 'supportEmail'],
      };
      const target = Object.keys(tabFor).find((t) => tabFor[t].some((f) => bad.includes(f)));
      if (target) setTab(target);
      // Name the problem. "Some settings need fixing" left the merchant hunting
      // across four tabs, and if the bad value came off a stored row rather
      // than something they just typed, there was nothing obvious to find.
      toast.error(
        bad.length > 1 ? `${found[bad[0]]} (+${bad.length - 1} more)` : found[bad[0]],
      );
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.post(
        `${API_CONFIG.BASE_URL}/api/appearance/save`,
        appearance,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );
      if (res.data?.success) {
        savedRef.current = res.data.data?.widget?.appearance || appearance;
        setAppearance(savedRef.current);
        setShowErrors(false);
        toast.success('Saved. Refresh your site to see the change.');
      } else {
        toast.error('Save failed. Please try again.');
      }
    } catch (err) {
      const details = err?.response?.data?.details;
      if (Array.isArray(details) && details.length) {
        const mapped = {};
        details.forEach((d) => {
          mapped[d.param] = d.msg;
        });
        setErrors(mapped);
        toast.error(details[0].msg);
      } else {
        toast.error('Save failed. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const errFor = (field) => (showErrors ? errors[field] : undefined);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 pb-24 pt-6 sm:px-6">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Appearance
          </h1>
          <p className="mt-1 text-[14px] text-gray-500 dark:text-gray-400">
            Design the chat widget your visitors see.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-5 py-2.5 text-[14px] font-medium text-white transition disabled:opacity-40 dark:bg-white dark:text-black"
        >
          {saving ? <ButtonLoader /> : <Save className="h-4 w-4" />}
          {dirty ? 'Save changes' : 'Saved'}
        </button>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* ── Form ───────────────────────────────────────────────── */}
        <div className="lg:col-span-7">
          <nav className="mb-5 flex gap-1 rounded-xl bg-gray-100 p-1 dark:bg-white/[0.04]">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`flex-1 rounded-lg px-3 py-2 text-[13px] font-medium transition ${
                  tab === t.id
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-white/[0.1] dark:text-white'
                    : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>

          <div className="space-y-5">
            {tab === 'style' && (
              <StyleTab appearance={appearance} set={set} errFor={errFor} />
            )}
            {tab === 'branding' && (
              <BrandingTab
                appearance={appearance}
                set={set}
                errFor={errFor}
                canHideBranding={canHideBranding}
              />
            )}
            {tab === 'content' && (
              <ContentTab appearance={appearance} set={set} errFor={errFor} />
            )}
            {tab === 'home' && <HomeTab appearance={appearance} set={set} errFor={errFor} />}
          </div>
        </div>

        {/* ── Preview ────────────────────────────────────────────── */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-6">
            <ChatPreview settings={previewSettings} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Style
 * ------------------------------------------------------------------ */

function StyleTab({ appearance, set, errFor }) {
  return (
    <>
      <Section icon={Palette} title="Theme & colors" subtitle="The brand color drives most of the widget">
        <Field label="Theme">
          <SelectionCards
            options={THEME_OPTIONS}
            value={appearance.theme}
            onChange={(v) => set('theme', v)}
          />
        </Field>

        <ColorField
          label="Brand color"
          value={appearance.primaryColor}
          onChange={(v) => set('primaryColor', v)}
          error={errFor('primaryColor')}
          hint="Fills the header, launcher and the visitor's message bubbles."
        />

        <Field label="Header style">
          <SelectionCards
            options={HEADER_STYLE_OPTIONS}
            value={appearance.headerStyle}
            onChange={(v) => set('headerStyle', v)}
          />
        </Field>

        {appearance.headerStyle === 'gradient' && (
          <ColorField
            label="Gradient second color"
            value={appearance.gradientColor}
            onChange={(v) => set('gradientColor', v)}
            error={errFor('gradientColor')}
            allowEmpty
            emptyLabel="Auto (darker brand color)"
            hint="Leave empty and we derive a darker shade of your brand color."
          />
        )}
      </Section>

      <Section icon={Layout} title="Layout & size" subtitle="Where the widget sits and how big it opens">
        <Field label="Position">
          <SelectionCards
            options={POSITION_OPTIONS}
            value={appearance.position}
            onChange={(v) => set('position', v)}
          />
        </Field>

        <Field label="Panel size">
          <SelectionCards
            options={SIZE_OPTIONS}
            value={appearance.size}
            onChange={(v) => set('size', v)}
            columns={3}
          />
        </Field>

        <SliderField
          label="Corner radius"
          value={Number(appearance.borderRadius)}
          min={BORDER_RADIUS.min}
          max={BORDER_RADIUS.max}
          onChange={(v) => set('borderRadius', v)}
          presets={RADIUS_PRESETS}
        />
      </Section>

      <Section icon={MousePointerClick} title="Chat button" subtitle="The launcher visitors tap to open the chat">
        <div className="grid gap-5 sm:grid-cols-2">
          <ColorField
            label="Button color"
            value={appearance.buttonBgColor}
            onChange={(v) => set('buttonBgColor', v)}
            error={errFor('buttonBgColor')}
          />
          <ColorField
            label="Icon color"
            value={appearance.buttonIconColor}
            onChange={(v) => set('buttonIconColor', v)}
            error={errFor('buttonIconColor')}
          />
        </div>

        <Field label="Icon">
          <SelectionCards
            options={BUTTON_ICON_OPTIONS}
            value={appearance.buttonIcon}
            onChange={(v) => set('buttonIcon', v)}
            columns={4}
          />
        </Field>

        <ToggleRow
          label="Open automatically"
          description="Opens the panel on page load instead of waiting for a click."
          checked={appearance.autoOpen}
          onChange={(v) => set('autoOpen', v)}
        />

        {/* Only meaningful while the home tab exists — with it off, chat is the
            only view the panel can open on, so offering the choice would be a
            control that changes nothing. */}
        {appearance.showHomeTab && (
          <Field
            label="Opens on"
            hint="Which view visitors land on. Turn the home tab off under Home & channels to always open in chat."
          >
            <SelectionCards
              options={DEFAULT_VIEW_OPTIONS}
              value={appearance.defaultView}
              onChange={(v) => set('defaultView', v)}
            />
          </Field>
        )}
      </Section>

      <Section icon={Type} title="Typography & spacing" subtitle="Font and how much room messages get">
        <Field label="Font" error={errFor('fontFamily')}>
          <SearchSelect
            value={appearance.fontFamily}
            options={FONT_CHOICES}
            onChange={(v) => set('fontFamily', v)}
            placeholder="Search 46 fonts…"
            // Each row previews the face it names, so the list is scannable by
            // shape instead of only by name.
            renderOption={(o) => <span style={{ fontFamily: `'${o.value}', sans-serif` }}>{o.label}</span>}
          />
        </Field>

        <Field label="Message spacing">
          <SelectionCards
            options={SPACING_OPTIONS}
            value={appearance.messageSpacing}
            onChange={(v) => set('messageSpacing', v)}
            columns={3}
          />
        </Field>
      </Section>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Branding
 * ------------------------------------------------------------------ */

function BrandingTab({ appearance, set, errFor, canHideBranding }) {
  return (
    <>
      <Section icon={Sparkles} title="Assistant" subtitle="The name visitors see in the header">
        <Field
          label="Assistant name"
          error={errFor('botName')}
          counter={<Counter value={(appearance.botName || '').length} max={NAME_MAX_CHARS} />}
          hint="Also the source of the initials shown when no logo is set."
        >
          <TextInput
            value={appearance.botName || ''}
            maxLength={NAME_MAX_CHARS}
            placeholder="Support"
            onChange={(e) => set('botName', e.target.value)}
            error={errFor('botName')}
          />
        </Field>
      </Section>

      <LogoCard appearance={appearance} set={set} />

      <Section icon={Lock} title="Powered by" subtitle="The PaperBrainz credit at the bottom of the panel">
        <ToggleRow
          label="Hide PaperBrainz branding"
          description={
            canHideBranding
              ? 'Removes the credit strip from your widget.'
              : 'Available on the Pro plan.'
          }
          checked={canHideBranding && appearance.hideBranding}
          disabled={!canHideBranding}
          onChange={(v) => set('hideBranding', v)}
          badge={
            !canHideBranding && (
              <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white dark:bg-white dark:text-black">
                Pro
              </span>
            )
          }
        />
      </Section>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Logo — upload, crop, framing
 * ------------------------------------------------------------------ */

function LogoCard({ appearance, set }) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [recropping, setRecropping] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileRef = useRef(null);

  const hasLogo = !!appearance.companyLogo;
  const logoSrc = schema.resolveLogoUrl(appearance.companyLogo, API_CONFIG.BASE_URL);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  });

  /** Shared validate-then-crop entry point for both click and drop. */
  const accept = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Images must be under 5MB.');
      return;
    }
    setRecropping(false);
    const reader = new FileReader();
    reader.onload = (e) => setCropSrc(e.target.result);
    reader.readAsDataURL(file);
  };

  const upload = async (blob) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('logo', new File([blob], 'logo.png', { type: 'image/png' }));
      const res = await fetch(`${API_CONFIG.BASE_URL}/api/appearance/upload-logo`, {
        method: 'POST',
        headers: authHeaders(),
        body: form,
      });
      const data = await res.json();
      if (!res.ok || !data.success || !data.data?.logoUrl) {
        throw new Error(data.message || `Upload failed (${res.status})`);
      }
      // The upload endpoint persists the URL itself; mirror it into local state
      // so the preview updates without waiting for a save.
      set('companyLogo', data.data.logoUrl);
      toast.success('Logo uploaded.');
    } catch (err) {
      toast.error(err.message || 'Could not upload that image.');
    } finally {
      setUploading(false);
    }
  };

  /**
   * Re-crop what is already stored without asking for the file again. Fetches
   * the served logo as a blob and converts to a data URL, because the cropper's
   * canvas would otherwise be CORS-tainted and refuse to export.
   */
  const recrop = async () => {
    try {
      const res = await fetch(logoSrc);
      if (!res.ok) throw new Error(`Could not load the logo (${res.status})`);
      const blob = await res.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      setRecropping(true);
      setCropSrc(dataUrl);
    } catch {
      toast.error('Could not open that logo for cropping.');
    }
  };

  const remove = async () => {
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}/api/appearance/logo`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error();
      set('companyLogo', '');
      // Leave no toggle pointing at a logo that is gone.
      set('showCompanyLogo', false);
      set('useLogoAsButtonIcon', false);
      toast.success('Logo removed.');
    } catch {
      toast.error('Could not remove the logo.');
    } finally {
      setConfirmDelete(false);
    }
  };

  return (
    <>
      <Section icon={ImageIcon} title="Logo" subtitle="Shown in the header, next to replies, and optionally on the launcher">
        {/* One component that adapts across empty / uploaded / uploading rather
            than a card nested inside a card. */}
        {!hasLogo ? (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              accept(e.dataTransfer?.files?.[0]);
            }}
            onClick={() => fileRef.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition ${
              dragging
                ? 'border-gray-900 bg-gray-50 dark:border-white/50 dark:bg-white/[0.04]'
                : 'border-gray-200 hover:border-gray-300 dark:border-white/[0.12] dark:hover:border-white/25'
            }`}
          >
            <UploadCloud className="mb-2 h-6 w-6 text-gray-400" />
            <p className="text-[13.5px] font-medium text-gray-700 dark:text-gray-200">
              Drop an image or click to choose
            </p>
            <p className="mt-1 text-[12px] text-gray-500 dark:text-gray-400">
              PNG, JPG, GIF or WebP &middot; up to 5MB
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-4 rounded-xl border border-gray-200 p-4 dark:border-white/[0.1]">
            <img
              src={logoSrc}
              alt="Your logo"
              className="h-14 w-14 flex-shrink-0 rounded-lg object-cover"
            />
            <div className="flex flex-1 flex-wrap gap-2">
              <button
                type="button"
                onClick={recrop}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-[12.5px] font-medium text-gray-700 hover:bg-gray-50 dark:border-white/[0.12] dark:text-gray-200 dark:hover:bg-white/[0.05]"
              >
                <Crop className="h-3.5 w-3.5" /> Re-crop
              </button>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-[12.5px] font-medium text-gray-700 hover:bg-gray-50 dark:border-white/[0.12] dark:text-gray-200 dark:hover:bg-white/[0.05]"
              >
                <UploadCloud className="h-3.5 w-3.5" /> Replace
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-[12.5px] font-medium text-red-600 hover:bg-red-50 dark:border-white/[0.12] dark:text-red-400 dark:hover:bg-red-500/10"
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            accept(e.target.files?.[0]);
            // Reset so re-picking the same file still fires onChange.
            e.target.value = '';
          }}
        />

        {uploading && (
          <p className="text-[12.5px] text-gray-500 dark:text-gray-400">Uploading&hellip;</p>
        )}

        {hasLogo && (
          <>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Shape" hint="Applies to the header and reply avatars.">
                <SelectionCards
                  options={LOGO_SHAPE_OPTIONS}
                  value={appearance.logoShape}
                  onChange={(v) => set('logoShape', v)}
                  columns={3}
                />
              </Field>
              <Field label="Fit">
                <SelectionCards
                  options={LOGO_FIT_OPTIONS}
                  value={appearance.logoFit}
                  onChange={(v) => set('logoFit', v)}
                />
              </Field>
            </div>

            <Field label="Padding" hint="Room around the mark inside its frame.">
              <div className="flex gap-2">
                {LOGO_PADDING_STEPS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => set('logoPadding', p)}
                    className={`flex-1 rounded-lg border px-3 py-2 text-[12.5px] font-medium transition ${
                      Number(appearance.logoPadding) === p
                        ? 'border-gray-900 bg-gray-50 text-gray-900 dark:border-white/60 dark:bg-white/[0.06] dark:text-white'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-white/[0.08] dark:text-gray-300'
                    }`}
                  >
                    {p === 0 ? 'None' : `${p}px`}
                  </button>
                ))}
              </div>
            </Field>

            <ToggleRow
              label="Show in the header"
              description="Displays your logo beside the assistant name."
              checked={appearance.showCompanyLogo}
              onChange={(v) => set('showCompanyLogo', v)}
            />
            <ToggleRow
              label="Use as the chat button"
              description="Replaces the launcher icon with your logo."
              checked={appearance.useLogoAsButtonIcon}
              onChange={(v) => set('useLogoAsButtonIcon', v)}
            />
          </>
        )}
      </Section>

      {cropSrc && (
        <ImageCropModal
          isOpen
          imageSrc={cropSrc}
          onClose={() => {
            setCropSrc(null);
            setRecropping(false);
          }}
          onCropComplete={(blob) => {
            setCropSrc(null);
            setRecropping(false);
            upload(blob);
          }}
        />
      )}

      <ConfirmModal
        isOpen={confirmDelete}
        title="Remove logo?"
        message="Your widget will fall back to the assistant's initials."
        confirmText="Remove"
        onConfirm={remove}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Content
 * ------------------------------------------------------------------ */

function ContentTab({ appearance, set, errFor }) {
  const [draft, setDraft] = useState('');
  const starters = appearance.customSuggestions || [];

  const addStarter = () => {
    const text = draft.trim();
    if (!text) return;
    if (starters.length >= MAX_STARTERS) {
      toast.error(`Up to ${MAX_STARTERS} starters.`);
      return;
    }
    set('customSuggestions', [...starters, text.slice(0, STARTER_MAX_CHARS)]);
    setDraft('');
  };

  return (
    <>
      <Section icon={MessageSquare} title="Messages" subtitle="What visitors read before they type">
        <Field label="Welcome title">
          <TextInput
            value={appearance.welcomeTitle || ''}
            placeholder="Hi there! 👋"
            onChange={(e) => set('welcomeTitle', e.target.value)}
          />
        </Field>

        <Field label="Welcome message">
          <TextArea
            rows={3}
            value={appearance.greeting || ''}
            placeholder="Ask us anything — we usually reply in a few seconds."
            onChange={(e) => set('greeting', e.target.value)}
          />
        </Field>

        <Field label="Input placeholder">
          <TextInput
            value={appearance.placeholder || ''}
            placeholder="Type your message…"
            onChange={(e) => set('placeholder', e.target.value)}
          />
        </Field>
      </Section>

      <Section
        icon={Sparkles}
        title="Conversation starters"
        subtitle="Tappable questions on the home screen"
      >
        <ToggleRow
          label="Show starters"
          description="Turn off to open straight into an empty chat."
          checked={appearance.showSuggestions}
          onChange={(v) => set('showSuggestions', v)}
        />

        {appearance.showSuggestions && (
          <>
            {starters.length === 0 && (
              <p className="rounded-xl bg-gray-50 px-4 py-3 text-[12.5px] leading-relaxed text-gray-500 dark:bg-white/[0.03] dark:text-gray-400">
                None yet. Add the questions your customers actually ask &mdash; generic ones
                like &ldquo;How can you help me?&rdquo; get ignored.
              </p>
            )}

            {starters.length > 0 && (
              <ul className="space-y-2">
                {starters.map((s, i) => (
                  <li
                    // Index only — keying on the value remounts the row on every
                    // keystroke, which blows away focus after a single letter.
                    key={i}
                    className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 dark:border-white/[0.1]"
                  >
                    <input
                      value={s}
                      maxLength={STARTER_MAX_CHARS}
                      onChange={(e) => {
                        const next = [...starters];
                        next[i] = e.target.value;
                        set('customSuggestions', next);
                      }}
                      className="flex-1 bg-transparent text-[13.5px] text-gray-900 focus:outline-none dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        set(
                          'customSuggestions',
                          starters.filter((_, idx) => idx !== i),
                        )
                      }
                      aria-label="Remove starter"
                      className="text-gray-400 transition hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {errFor('customSuggestions') && (
              <p className="text-[12px] text-red-600 dark:text-red-400">
                {errFor('customSuggestions')}
              </p>
            )}

            {starters.length < MAX_STARTERS && (
              <div className="flex gap-2">
                <TextInput
                  value={draft}
                  maxLength={STARTER_MAX_CHARS}
                  placeholder="Do you ship to Canada?"
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addStarter();
                    }
                  }}
                />
                {/* Plus, never a trash can — this action creates something. */}
                <button
                  type="button"
                  onClick={addStarter}
                  className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-xl border border-gray-200 px-4 text-[13px] font-medium text-gray-700 hover:bg-gray-50 dark:border-white/[0.12] dark:text-gray-200 dark:hover:bg-white/[0.05]"
                >
                  <Plus className="h-4 w-4" /> Add
                </button>
              </div>
            )}
          </>
        )}
      </Section>

      <Section icon={Type} title="Typing indicator" subtitle="Shown while the assistant is composing">
        <SelectionCards
          options={TYPING_OPTIONS}
          value={appearance.typingIndicator}
          onChange={(v) => set('typingIndicator', v)}
          columns={3}
        />
      </Section>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Home & channels
 * ------------------------------------------------------------------ */

function HomeTab({ appearance, set, errFor }) {
  return (
    <>
      <Section icon={Home} title="Home screen" subtitle="A landing tab in front of the chat">
        <ToggleRow
          label="Show the home tab"
          description="Turn off for a chat-only widget with no tab bar."
          checked={appearance.showHomeTab}
          onChange={(v) => set('showHomeTab', v)}
        />
      </Section>

      <Section
        icon={MessageSquare}
        title="Contact channels"
        subtitle="Extra ways to reach you, listed on the home screen"
      >
        <ChannelRow
          label="WhatsApp"
          description="Opens a WhatsApp chat with your number."
          enabled={appearance.showWhatsapp}
          onToggle={(v) => set('showWhatsapp', v)}
          value={appearance.whatsappNumber}
          onChange={(v) => set('whatsappNumber', v)}
          placeholder="+1 555 000 1234"
          error={errFor('whatsappNumber')}
        />
        <ChannelRow
          label="Email"
          description="Opens the visitor's mail app."
          enabled={appearance.showEmail}
          onToggle={(v) => set('showEmail', v)}
          value={appearance.emailAddress}
          onChange={(v) => set('emailAddress', v)}
          placeholder="hello@yourbusiness.com"
          error={errFor('emailAddress')}
        />
        <ChannelRow
          label="Phone"
          description="Starts a call on mobile."
          enabled={appearance.showPhone}
          onToggle={(v) => set('showPhone', v)}
          value={appearance.phoneNumber}
          onChange={(v) => set('phoneNumber', v)}
          placeholder="+1 555 000 1234"
          error={errFor('phoneNumber')}
        />
      </Section>

      <Section
        icon={LifeBuoy}
        title="Support contact"
        subtitle="Shown if the assistant hits its monthly limit"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Support email" error={errFor('supportEmail')}>
            <TextInput
              value={appearance.supportEmail || ''}
              placeholder="support@yourbusiness.com"
              onChange={(e) => set('supportEmail', e.target.value)}
              error={errFor('supportEmail')}
            />
          </Field>
          <Field label="Support phone">
            <TextInput
              value={appearance.supportPhone || ''}
              placeholder="+1 555 000 1234"
              onChange={(e) => set('supportPhone', e.target.value)}
            />
          </Field>
        </div>
      </Section>
    </>
  );
}

/**
 * A channel is a toggle plus its value. The input only appears once the toggle
 * is on, and saving is blocked while it is empty — a half-configured channel
 * would otherwise ship a dead link onto the customer's site.
 */
function ChannelRow({ label, description, enabled, onToggle, value, onChange, placeholder, error }) {
  return (
    <div className="space-y-2">
      <ToggleRow label={label} description={description} checked={enabled} onChange={onToggle} />
      {enabled && (
        <div className="pl-1">
          <TextInput
            value={value || ''}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            error={error}
          />
          {error && <p className="mt-1.5 text-[12px] text-red-600 dark:text-red-400">{error}</p>}
        </div>
      )}
    </div>
  );
}
